/**
 * Query hooks for Settings → Webhooks (ADR inbound-webhooks). Screens
 * import this, never the server.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { orpc } from '@/adapters/rpc/client'
import type { AlertRule } from '@/core/data-health'
import { useInvalidate } from '@/features/shared/hooks/use-invalidate'
import { statusQuery } from '@/lib/live-data'

const showRefusal = (error: Error) => toast.error(error.message || 'That change was not allowed.')

const useInvalidateWebhooks = () => useInvalidate('webhooks')

/**
 * Status views (docs/LIVE-DATA.md): a webhook's health, its runs, its alert's
 * flag history and the Admin banner follow changes made anywhere — every 30
 * seconds while the page is open, on focus, and at once after any change
 * here (each mutation invalidates every webhook query).
 */
const EVERY = 30_000

export function useWebhooksQuery() {
  return useQuery(statusQuery(orpc.webhooks.list.queryOptions(), EVERY))
}

export function useWebhookQuery(id: string) {
  return useQuery(statusQuery(orpc.webhooks.get.queryOptions({ input: { id } }), EVERY))
}

export function useDeliveriesQuery(id: string, page: number, pageSize: number) {
  return useQuery(
    statusQuery(
      {
        ...orpc.webhooks.deliveries.queryOptions({ input: { id, page, pageSize } }),
        placeholderData: keepPreviousData,
      },
      EVERY,
    ),
  )
}

/** The no-data alert's flag history, newest first. */
export function useAlertFlagsQuery(id: string, page: number, pageSize: number) {
  return useQuery(
    statusQuery(
      {
        ...orpc.webhooks.alertFlags.queryOptions({ input: { id, page, pageSize } }),
        placeholderData: keepPreviousData,
      },
      EVERY,
    ),
  )
}

/** Webhooks that need an Admin now — the banner. Only asked for when `enabled` (an Admin). */
export function useWebhookAlarmsQuery(enabled: boolean) {
  return useQuery({
    ...statusQuery(orpc.webhooks.alarms.queryOptions(), 60_000),
    enabled,
  })
}

/**
 * Saves a webhook's no-data alert rule, optimistically: the screen shows the
 * new rule at once and puts the old one back if the save fails. No toast —
 * the card's own "Saving… / Saved / Couldn't save" says it.
 */
export function useSaveAlertRule(id: string) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateWebhooks()
  const key = orpc.webhooks.get.queryKey({ input: { id } })
  const mutation = useMutation(
    orpc.webhooks.setAlerts.mutationOptions({
      onMutate: async (input) => {
        await queryClient.cancelQueries({ queryKey: key })
        const previous = queryClient.getQueryData(key)
        if (previous) queryClient.setQueryData(key, { ...previous, alertRule: input.alertRule })
        return { previous }
      },
      onError: (_error, _input, context) => {
        if (context?.previous) queryClient.setQueryData(key, context.previous)
      },
      onSettled: () => invalidate(),
    }),
  )
  const { mutateAsync } = mutation
  return useCallback(
    async (alertRule: AlertRule) => {
      await mutateAsync({ id, alertRule })
    },
    [id, mutateAsync],
  )
}

/** One delivery's payload, masked on the server — fetched only when opened. */
export function usePayloadQuery(id: string | null) {
  return useQuery({
    ...orpc.webhooks.payload.queryOptions({ input: { id: id ?? '' } }),
    enabled: Boolean(id),
  })
}

export function useDeletedWebhooksQuery() {
  return useQuery(statusQuery(orpc.webhooks.listDeleted.queryOptions(), EVERY))
}

export function useRestoreWebhook() {
  const invalidate = useInvalidateWebhooks()
  return useMutation(
    orpc.webhooks.restore.mutationOptions({ onSuccess: invalidate, onError: showRefusal }),
  )
}

/**
 * Delete a webhook, recoverably (CLAUDE.md "Deleting is recoverable"):
 * it moves to Recently deleted, its address refuses everything at once, and a
 * toast offers Undo.
 */
export function useDeleteWebhook() {
  const invalidate = useInvalidateWebhooks()
  const restore = useRestoreWebhook()
  const remove = useMutation(
    orpc.webhooks.remove.mutationOptions({ onSuccess: invalidate, onError: showRefusal }),
  )
  return {
    isPending: remove.isPending,
    async run(webhook: { id: string; name: string }) {
      await remove.mutateAsync({ id: webhook.id })
      toast(`${webhook.name} deleted`, {
        action: { label: 'Undo', onClick: () => restore.mutate({ id: webhook.id }) },
      })
    },
  }
}

export function usePurgeWebhook() {
  const invalidate = useInvalidateWebhooks()
  return useMutation(
    orpc.webhooks.purge.mutationOptions({ onSuccess: invalidate, onError: showRefusal }),
  )
}

export function useCreateWebhook() {
  const invalidate = useInvalidateWebhooks()
  return useMutation(orpc.webhooks.create.mutationOptions({ onSuccess: invalidate }))
}

export function useRotateWebhook() {
  const invalidate = useInvalidateWebhooks()
  return useMutation(
    orpc.webhooks.rotate.mutationOptions({ onSuccess: invalidate, onError: showRefusal }),
  )
}

export function useSetWebhookEnabled() {
  const invalidate = useInvalidateWebhooks()
  return useMutation(
    orpc.webhooks.setEnabled.mutationOptions({ onSuccess: invalidate, onError: showRefusal }),
  )
}

export function useSendTest() {
  const invalidate = useInvalidateWebhooks()
  return useMutation(
    orpc.webhooks.sendTest.mutationOptions({
      onSuccess: invalidate,
      onError: showRefusal,
    }),
  )
}

/** Re-run a delivery through the public address: it lands as a new row (ADR inbound-webhooks). */
export function useRerunDelivery() {
  const queryClient = useQueryClient()
  return useMutation(
    orpc.webhooks.rerun.mutationOptions({
      // A project's reader may have changed any record it feeds, so refresh
      // everything rather than name each screen.
      onSuccess: () => queryClient.invalidateQueries(),
      onError: showRefusal,
    }),
  )
}
